import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BadgePercent, Loader2, Store, MousePointerClick, Euro, Users, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default function ProAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [social, setSocial] = useState({ hosts: "", welcomebooks: "", guests: "" });
  const [savedMsg, setSavedMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, soc] = await Promise.all([
          axios.get(`${API}/pro/admin/stats`, auth()),
          axios.get(`${API}/pro/social`),
        ]);
        setData(s.data); setSocial(soc.data);
      } catch (e) { setData({ error: e?.response?.status === 403 ? "Accesso riservato agli admin" : "Errore" }); }
      finally { setLoading(false); }
    })();
  }, []);

  const saveSocial = async () => {
    await axios.put(`${API}/pro/admin/social`, social, auth());
    setSavedMsg("Salvato!"); setTimeout(() => setSavedMsg(""), 2000);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  if (data?.error) return <div className="min-h-screen flex items-center justify-center text-zinc-500" data-testid="pro-admin-error">{data.error}</div>;

  const t = data.totals;
  const cards = [
    { i: Store, n: t.active_promos, sub: `/${t.promos}`, l: "Promo attive" },
    { i: MousePointerClick, n: t.total_clicks, l: "Click totali" },
    { i: Users, n: t.subscribed, sub: `/${t.pro_users}`, l: "Abbonati Pro" },
    { i: Euro, n: `€${t.revenue_eur}`, l: "Incassato" },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="pro-admin">
      <header className="bg-zinc-900 text-white px-4 py-5">
        <div className="max-w-5xl mx-auto flex items-center gap-2"><BadgePercent className="w-6 h-6 shrink-0" /><h1 className="text-lg sm:text-xl font-black leading-tight">Hostland Pro — Amministrazione</h1></div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cards.map((c) => (
            <motion.div key={c.l} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-white border border-zinc-100 p-5">
              <c.i className="w-5 h-5 text-violet-500 mb-2" />
              <div className="text-3xl font-black tabular-nums">{c.n}<span className="text-base text-zinc-400 font-bold">{c.sub || ""}</span></div>
              <div className="text-xs text-zinc-500 font-semibold">{c.l}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white border border-zinc-100 p-5 min-w-0">
            <h2 className="font-black mb-3">Promo per città</h2>
            <div className="space-y-1.5">
              {data.per_city.map((c) => (
                <div key={c.city} className="flex items-center justify-between text-sm"><span>{c.city}</span><span className="font-bold tabular-nums">{c.count}</span></div>
              ))}
              {data.per_city.length === 0 && <p className="text-sm text-zinc-400">Nessuna promo.</p>}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-zinc-100 p-5 min-w-0" data-testid="pro-social-editor">
            <h2 className="font-black mb-3">Numeri prova sociale (landing /pro)</h2>
            {[["hosts", "Host nella rete"], ["welcomebooks", "Welcome Book attivi"], ["guests", "Ospiti raggiunti"]].map(([k, l]) => (
              <div key={k} className="flex items-center gap-2 mb-2">
                <label className="text-sm text-zinc-500 w-32 sm:w-40 shrink-0">{l}</label>
                <input value={social[k] || ""} onChange={(e) => setSocial((s) => ({ ...s, [k]: e.target.value }))} className="flex-1 min-w-0 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm outline-none focus:border-zinc-900" data-testid={`pro-social-${k}`} />
              </div>
            ))}
            <button onClick={saveSocial} className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 text-white font-bold text-sm" data-testid="pro-social-save"><Save className="w-4 h-4" /> Salva</button>
            {savedMsg && <span className="ml-2 text-sm text-emerald-600 font-bold">{savedMsg}</span>}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-zinc-100 overflow-hidden">
          <h2 className="font-black px-5 pt-5 pb-2">Promozioni</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="text-left text-zinc-400 text-xs uppercase tracking-wider"><tr><th className="px-5 py-2 whitespace-nowrap">Attività</th><th className="px-3 py-2 whitespace-nowrap">Città</th><th className="px-3 py-2 whitespace-nowrap">Stato</th><th className="px-3 py-2 whitespace-nowrap">Click</th><th className="px-5 py-2 whitespace-nowrap">Host</th></tr></thead>
              <tbody>
                {data.promos.map((p) => (
                  <tr key={p.promo_id} className="border-t border-zinc-100" data-testid={`pro-admin-row-${p.promo_id}`}>
                    <td className="px-5 py-2 font-semibold whitespace-nowrap">{p.business_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{p.city}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{p.status === "active" ? <span className="text-emerald-600 font-bold">attiva</span> : <span className="text-amber-600">pausa</span>}</td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">{p.clicks}</td>
                    <td className="px-5 py-2 tabular-nums whitespace-nowrap">{p.active_hosts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.promos.length === 0 && <p className="text-sm text-zinc-400 p-5">Nessuna promo.</p>}
        </div>
      </main>
    </div>
  );
}
